import React, { ReactElement, useState } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  Badge,
  Box,
  Button,
  IconButton,
  ListItem,
  ListSubheader,
  Menu,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { useAccountListId } from 'src/hooks/useAccountListId';
import theme from 'src/theme';
import illustration13 from '../../../../../../images/drawkit/grape/drawkit-grape-pack-illustration-13.svg';
import { useAcknowledgeAllUserNotificationsMutation } from './AcknowledgeAllUserNotifications.generated';
import {
  GetNotificationsDocument,
  GetNotificationsQuery,
  useGetNotificationsQuery,
} from './GetNotificationsQuery.generated';
import NotificationMenuItem from './Item';

interface NotificationMenuProps {
  isInDrawer?: boolean;
}

const useStyles = makeStyles()(() => ({
  link: {
    textTransform: 'none',
    color: 'rgba(255,255,255,0.75)',
    transition: 'color 0.2s ease-in-out',
    '&:hover': {
      color: 'rgba(255,255,255,1)',
    },
  },
  menuPaper: {
    width: '50ch',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  menuList: {
    padding: 0,
  },
  menuButton: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  listSubheader: {
    outline: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 2,
  },
  listItemEmpty: {
    flexDirection: 'column',
    paddingBottom: theme.spacing(2),
  },
  img: {
    height: '150px',
    marginBottom: theme.spacing(2),
  },
  customBadge: {
    backgroundColor: theme.palette.progressBarYellow.main,
    color: 'white',
    fontWeight: '700',
  },
}));

export const NotificationContent = ({
  data,
  loading,
  onAcknowledgeAllClick,
  onClose,
  onFetchMore,
  isInDrawer,
}: {
  data: GetNotificationsQuery | undefined;
  loading: boolean;
  onAcknowledgeAllClick: () => void;
  onClose: () => void;
  onFetchMore: () => void;
  isInDrawer?: boolean;
}): ReactElement => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <ListSubheader className={classes.listSubheader}>
        <Box display="flex" flexDirection="row" justifyContent="center">
          <Box flexGrow={1} display="flex" alignItems="center">
            <Typography>{t('Notifications')}</Typography>
          </Box>
          <Box>
            <Button
              size="small"
              disabled={
                !data ||
                data.userNotifications.nodes.filter(({ read }) => !read)
                  .length === 0
              }
              onClick={onAcknowledgeAllClick}
            >
              {t('Mark all as read')}
            </Button>
          </Box>
        </Box>
      </ListSubheader>
      {data?.userNotifications?.nodes.map((item, index, nodes) => (
        <NotificationMenuItem
          key={item.id}
          item={item}
          previousItem={nodes[index - 1]}
          last={index + 1 === nodes.length && !loading}
          onClick={onClose}
          isInDrawer={isInDrawer}
        />
      ))}
      {!loading && data?.userNotifications?.pageInfo?.hasNextPage && (
        <ListItem>
          <Button
            className={classes.menuButton}
            variant="outlined"
            color="inherit"
            onClick={onFetchMore}
          >
            {t('Load More')}
          </Button>
        </ListItem>
      )}
      {!loading && data?.userNotifications.nodes.length === 0 && (
        <ListItem className={classes.listItemEmpty}>
          <img src={illustration13} className={classes.img} alt="empty" />
          <Typography>{t('No notifications to show.')}</Typography>
        </ListItem>
      )}
      {loading && (
        <Box data-testid="NotificationMenuLoading">
          <NotificationMenuItem />
          <NotificationMenuItem last={true} />
        </Box>
      )}
    </>
  );
};

const NotificationMenu = ({
  isInDrawer = false,
}: NotificationMenuProps): ReactElement => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const accountListId = useAccountListId();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();

  const { data, loading, fetchMore } = useGetNotificationsQuery({
    variables: {
      accountListId,
      after: null,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [acknoweldgeAllUserNotifications] =
    useAcknowledgeAllUserNotificationsMutation();

  const handleAcknowledgeAllClick = () => {
    acknoweldgeAllUserNotifications({
      variables: { accountListId },
      optimisticResponse: {
        acknowledgeAllUserNotifications: {
          notificationIds: [],
        },
      },
      update: (cache) => {
        const query = {
          query: GetNotificationsDocument,
          variables: {
            accountListId,
            after: null,
          },
        };
        const dataFromCache = cache.readQuery<GetNotificationsQuery>(query);
        const data = {
          userNotifications: {
            ...dataFromCache?.userNotifications,
            unreadCount: 0,
            nodes: dataFromCache?.userNotifications.nodes.map((node) => ({
              ...node,
              read: true,
            })),
          },
        };
        cache.writeQuery({ ...query, data });
      },
    });
    handleClose();
  };

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const handleFetchMore = () => {
    fetchMore({
      variables: { after: data?.userNotifications.pageInfo.endCursor },
    });
  };

  if (isInDrawer) {
    return (
      <NotificationContent
        data={data}
        loading={loading}
        onAcknowledgeAllClick={handleAcknowledgeAllClick}
        onClose={handleClose}
        onFetchMore={handleFetchMore}
        isInDrawer={true}
      />
    );
  }

  const unread = data?.userNotifications?.unreadCount;
  return (
    <>
      <IconButton
        className={classes.link}
        aria-label={
          unread
            ? t('Notifications ({{unread}} unread)', {
                unread,
              })
            : t('Notifications')
        }
        aria-controls="notification-menu"
        aria-haspopup="menu"
        onClick={handleClick}
      >
        <Badge
          badgeContent={unread}
          classes={{ badge: classes.customBadge }}
          max={99}
        >
          {unread !== 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableRestoreFocus={true}
        classes={{ paper: classes.menuPaper, list: classes.menuList }}
      >
        <NotificationContent
          data={data}
          loading={loading}
          onAcknowledgeAllClick={handleAcknowledgeAllClick}
          onClose={handleClose}
          onFetchMore={handleFetchMore}
        />
      </Menu>
    </>
  );
};

export default NotificationMenu;
